import React, { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { taskDetailApi } from "../../backend/client";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { TaskDialog, TaskMessage } from "./taskFormUi";

const MIN_COUNT = 1;
const MAX_COUNT = 20;

const createRow = () => ({
  id:
    crypto?.randomUUID?.() ||
    `${Date.now()}-${Math.random().toString(16).slice(2)}`,
  title: "",
  address: "",
  bedrooms: "",
  bathrooms: "",
  car_spaces: "",
  link: "",
  notes: "",
});

export default function PropertiesForm({
  isOpen,
  onClose,
  projectId,
  taskId,
  onSave,
}) {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const numericProjectId =
    projectId != null && /^\d+$/.test(String(projectId))
      ? Number(projectId)
      : null;
  const canonicalTaskId =
    taskId != null &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      String(taskId),
    )
      ? String(taskId)
      : null;

  const canAdd = rows.length < MAX_COUNT;
  const canSave =
    rows.length >= MIN_COUNT &&
    canonicalTaskId !== null &&
    rows.every(
      (row) => row.title?.trim() && (row.link?.trim() || row.address?.trim()),
    );

  useEffect(() => {
    if (!isOpen || !numericProjectId) return;

    (async () => {
      setError("");
      const { data, error } = await taskDetailApi.list("properties", {
        project_id: numericProjectId,
        order: "created_at",
        ascending: true,
      });

      if (error) {
        setError(error.message || "Failed to load properties");
      } else {
        setRows((data || []).slice(0, MAX_COUNT));
      }
    })();
  }, [isOpen, numericProjectId]);

  const addRow = () => {
    if (!canAdd) return;
    setRows((previous) => [...previous, createRow()]);
  };

  const removeRow = (index) =>
    setRows((previous) =>
      previous.filter((_, itemIndex) => itemIndex !== index),
    );
  const update = (index, field, value) =>
    setRows((previous) =>
      previous.map((row, itemIndex) =>
        itemIndex === index ? { ...row, [field]: value } : row,
      ),
    );

  const handleSave = async () => {
    if (!numericProjectId || !canSave) return;
    setSaving(true);
    setError("");

    try {
      const { data: existingRows, error: loadError } = await taskDetailApi.list(
        "properties",
        { project_id: numericProjectId },
      );
      if (loadError) throw loadError;

      const deleteResults = await Promise.all(
        (existingRows || []).map((row) =>
          taskDetailApi.delete("properties", row.id),
        ),
      );
      const deleteError = deleteResults.find((result) => result.error)?.error;
      if (deleteError) throw deleteError;

      const payload = rows.slice(0, MAX_COUNT).map((row) => ({
        project_id: numericProjectId,
        task_id: canonicalTaskId,
        title: row.title?.trim(),
        address: row.address?.trim() || null,
        bedrooms: row.bedrooms === "" ? null : Number(row.bedrooms),
        bathrooms: row.bathrooms === "" ? null : Number(row.bathrooms),
        car_spaces: row.car_spaces === "" ? null : Number(row.car_spaces),
        link: row.link?.trim() || null,
        notes: row.notes?.trim() || null,
      }));

      const { error: insertError } = await taskDetailApi.create(
        "properties",
        payload,
      );
      if (insertError) throw insertError;

      onSave?.();
      onClose?.();
    } catch (error) {
      setError(error.message || "Failed to save properties");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <TaskDialog
      title="Example Properties"
      description="Shortlist properties with either a link or address for each option."
      onClose={onClose}
      className="max-h-[90vh] max-w-4xl overflow-y-auto"
    >
      <div className="space-y-5">
        <TaskMessage>{error}</TaskMessage>

        <div className="space-y-4">
          {rows.map((row, index) => (
            <PropertyCard
              key={row.id || index}
              row={row}
              index={index}
              onRemove={() => removeRow(index)}
              onUpdate={(field, value) => update(index, field, value)}
            />
          ))}
        </div>

        <div className="flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
          <Button
            type="button"
            variant="outline"
            disabled={!canAdd}
            onClick={addRow}
          >
            <Plus className="h-4 w-4" />
            Add Property (max {MAX_COUNT})
          </Button>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!canSave || saving}
              onClick={handleSave}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          Each property needs a title and either a link or address.
        </p>
      </div>
    </TaskDialog>
  );
}

const PropertyCard = ({ row, index, onRemove, onUpdate }) => (
  <Card>
    <CardHeader className="flex-row items-center justify-between space-y-0">
      <CardTitle>Property {index + 1}</CardTitle>
      <Button type="button" variant="ghost" size="sm" onClick={onRemove}>
        <Trash2 className="h-4 w-4" />
        Remove
      </Button>
    </CardHeader>
    <CardContent className="grid gap-4 sm:grid-cols-2">
      <Field
        id={`property-title-${index}`}
        label="Title *"
        value={row.title || ""}
        onChange={(value) => onUpdate("title", value)}
        placeholder="e.g. 2-bed townhouse - Newstead"
      />
      <Field
        id={`property-link-${index}`}
        label="Link"
        value={row.link || ""}
        onChange={(value) => onUpdate("link", value)}
        placeholder="https://..."
      />
      <Field
        id={`property-address-${index}`}
        label="Address"
        value={row.address || ""}
        onChange={(value) => onUpdate("address", value)}
        placeholder="Street, suburb, state, postcode"
        className="sm:col-span-2"
      />
      <div className="grid gap-3 sm:col-span-2 sm:grid-cols-3">
        <Field
          id={`property-bedrooms-${index}`}
          label="Beds"
          type="number"
          min="0"
          value={row.bedrooms ?? ""}
          onChange={(value) => onUpdate("bedrooms", value)}
        />
        <Field
          id={`property-bathrooms-${index}`}
          label="Baths"
          type="number"
          min="0"
          value={row.bathrooms ?? ""}
          onChange={(value) => onUpdate("bathrooms", value)}
        />
        <Field
          id={`property-car-spaces-${index}`}
          label="Car"
          type="number"
          min="0"
          value={row.car_spaces ?? ""}
          onChange={(value) => onUpdate("car_spaces", value)}
        />
      </div>
      <TextAreaField
        id={`property-notes-${index}`}
        label="Notes"
        value={row.notes || ""}
        onChange={(value) => onUpdate("notes", value)}
        className="sm:col-span-2"
      />
    </CardContent>
  </Card>
);

const Field = ({ id, label, value, onChange, className = "", ...props }) => (
  <div className={`space-y-2 ${className}`}>
    <Label htmlFor={id}>{label}</Label>
    <Input
      id={id}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      {...props}
    />
  </div>
);

const TextAreaField = ({ id, label, value, onChange, className = "" }) => (
  <div className={`space-y-2 ${className}`}>
    <Label htmlFor={id}>{label}</Label>
    <Textarea
      id={id}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      rows={3}
    />
  </div>
);
